import { formatDistanceUnit } from '@boatnav/core';
import { useEffect } from 'react';
import { useMapController } from '../map/MapShell.js';
import { useThemeStore } from '../../app/themeStore.js';
import { useUnitsStore } from '../../app/unitsStore.js';
import { useMeasureStore } from './measureStore.js';
import { computeMeasureTotals } from './measureMetrics.js';
import { useDeclinationDeg, formatCourseRef } from './courseFormat.js';
import { msg } from '../../lib/messages.js';
import './measure.css';

/**
 * Tap-to-measure distance + bearing (spec 14A), the paper-chart divider
 * replacement. Mounted unconditionally on the Navigate screen (same shape as
 * TrailLayer/GotoLayer) — `onMapClick` only registers while active, so it
 * never competes with any other tap consumer on that screen. A measuring tap
 * is not a `dragstart`, so it never cancels follow mode; measuring works
 * while following.
 */
export function MeasureLayer(): JSX.Element | null {
  const controller = useMapController();
  const active = useMeasureStore((s) => s.active);
  const points = useMeasureStore((s) => s.points);
  const addPoint = useMeasureStore((s) => s.addPoint);
  const undo = useMeasureStore((s) => s.undo);
  const exit = useMeasureStore((s) => s.exit);
  const theme = useThemeStore((s) => s.theme); // re-tint on day/night
  const distanceUnit = useUnitsStore((s) => s.distance);
  const courseRef = useUnitsStore((s) => s.courseRef);
  const declDeg = useDeclinationDeg();

  // Draw/clear the layer. Exiting (toggle off or unmounting, e.g. leaving
  // the screen) always clears it.
  useEffect(() => {
    if (!controller) return;
    if (!active) {
      controller.clearMeasure();
      return;
    }
    controller.drawMeasure(points);
    return () => controller.clearMeasure();
  }, [controller, active, points, theme]);

  useEffect(() => {
    if (!controller || !active) return undefined;
    return controller.onMapClick((lat, lon) => addPoint(lat, lon));
  }, [controller, active, addPoint]);

  // Leaving the screen (component unmount) also resets the mode itself, not
  // just the map layer — re-entering Navigate starts fresh, per spec.
  useEffect(() => {
    return () => {
      useMeasureStore.getState().exit();
    };
  }, []);

  if (!active) return null;

  const totals = computeMeasureTotals(points);
  const totalText = formatDistanceUnit(totals.totalNm, distanceUnit);
  const lastLegText =
    totals.lastLegNm != null ? formatDistanceUnit(totals.lastLegNm, distanceUnit) : null;
  const brgText =
    totals.lastLegBrg != null ? formatCourseRef(totals.lastLegBrg, declDeg, courseRef) : null;

  return (
    <div className="measure-chip">
      {points.length === 0 ? (
        <span className="measure-chip__text">{msg.measure.hint}</span>
      ) : (
        <span className="measure-chip__text numeral">
          <b>{totalText}</b>
          {lastLegText != null ? ` · ${msg.measure.lastLeg} ${lastLegText}` : ''}
          {brgText != null ? ` · ${brgText}` : ''}
        </span>
      )}
      <button
        type="button"
        className="measure-chip__undo"
        disabled={points.length === 0}
        onClick={undo}
      >
        {msg.measure.undo}
      </button>
      <button
        type="button"
        className="measure-chip__close"
        aria-label={msg.measure.close}
        onClick={exit}
      >
        ✕
      </button>
    </div>
  );
}
